import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Formats a number as Indian Rupee currency with Indian numbering system
 * Example: 311396.24 -> ₹3,11,396.24
 */
export function formatINR(amount: number): string {
  // Handle negative numbers
  const isNegative = amount < 0;
  const absAmount = Math.abs(amount);
  
  // Split into integer and decimal parts
  const parts = absAmount.toFixed(2).split('.');
  let integerPart = parts[0];
  const decimalPart = parts[1];
  
  // Apply Indian numbering system
  // First 3 digits from right, then every 2 digits
  let formatted = '';
  const length = integerPart.length;
  
  if (length <= 3) {
    formatted = integerPart;
  } else {
    // Take last 3 digits
    formatted = integerPart.slice(-3);
    integerPart = integerPart.slice(0, -3);
    
    // Add remaining digits in groups of 2
    while (integerPart.length > 0) {
      const group = integerPart.slice(-2);
      formatted = group + ',' + formatted;
      integerPart = integerPart.slice(0, -2);
    }
  }
  
  const sign = isNegative ? '-' : '';
  return `${sign}₹${formatted}.${decimalPart}`;
}

/**
 * Formats a number in compact Indian format: K (thousands), L (lakhs), Cr (crores)
 * Examples: 95073.85 -> ₹95.07 K, 165960.25 -> ₹1.66 L, 10000000 -> ₹1.00 Cr
 */
export function formatCompact(amount: number): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';
  
  if (absValue >= 10000000) {
    // Crores (1 Cr = 10,000,000)
    return `${sign}₹${(absValue / 10000000).toFixed(2)} Cr`;
  } else if (absValue >= 100000) {
    // Lakhs (1 L = 100,000)
    return `${sign}₹${(absValue / 100000).toFixed(2)} L`;
  } else if (absValue >= 1000) {
    // Thousands (1 K = 1,000)
    return `${sign}₹${(absValue / 1000).toFixed(2)} K`;
  } else {
    return `${sign}₹${absValue.toFixed(2)}`;
  }
}
